import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isUnlimited } from '../../types/subscription';

export type LimitType = 'products' | 'locations' | 'sales';

interface UsageIndicatorProps {
  limitType: LimitType;
  current: number;
  limit: number;
  showLabel?: boolean;
}

const StyledProgressContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(1),
}));

const StyledLinearProgress = styled(LinearProgress)<{ usagepercentage: number }>(
  ({ theme, usagepercentage }) => {
    let color = theme.palette.success.main;

    if (usagepercentage >= 90) {
      color = theme.palette.error.main;
    } else if (usagepercentage >= 70) {
      color = theme.palette.warning.main;
    }

    return {
      height: 8,
      borderRadius: 4,
      backgroundColor: theme.palette.grey[200],
      '& .MuiLinearProgress-bar': {
        backgroundColor: color,
        borderRadius: 4,
      },
    };
  }
);

const LabelContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
}));

const getLimitTypeLabel = (limitType: LimitType): string => {
  const labels: Record<LimitType, string> = {
    products: 'Products',
    locations: 'Locations',
    sales: 'Sales',
  };
  return labels[limitType];
};

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

export const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  limitType,
  current,
  limit,
  showLabel = true,
}) => {
  const unlimited = isUnlimited(limit);
  const usagePercentage = unlimited ? 0 : Math.min(100, (current / limit) * 100);
  const label = getLimitTypeLabel(limitType);

  return (
    <StyledProgressContainer>
      {showLabel && (
        <LabelContainer>
          <Typography variant="body2" color="textSecondary" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="body2" color="textPrimary" fontWeight={600}>
            {formatNumber(current)} / {unlimited ? 'Unlimited' : formatNumber(limit)}
          </Typography>
        </LabelContainer>
      )}
      <StyledLinearProgress
        variant="determinate"
        value={usagePercentage}
        usagepercentage={usagePercentage}
      />
    </StyledProgressContainer>
  );
};

export default UsageIndicator;
